import React, { useEffect, useState, useMemo } from 'react';
import { Layers, Crosshair, ZoomIn, ZoomOut } from 'lucide-react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import './MapView.css';


const STATIC_BATONS = [
  {
    id: 0,
    lat: 16.5062,
    lng: 80.6480,
    status: 'On Duty',
    unit: 'Police Constable',
  },
];

// ── Fixes Leaflet map size after CSS layout settles on mobile ──
const MapResizer = () => {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 150);
    return () => clearTimeout(timer);
  }, [map]);
  return null;
};

const ZoomButtons = () => {
  const map = useMap();
  return (
    <div className="map-controls">
      <button className="map-btn" onClick={() => map.zoomIn()}><ZoomIn size={16}/></button>
      <button className="map-btn" onClick={() => map.zoomOut()}><ZoomOut size={16}/></button>
      <button className="map-btn"><Layers size={16}/></button>
      <button className="map-btn" onClick={() => map.setView([16.5062, 80.6480], 12)}><Crosshair size={16}/></button>
    </div>
  );
};

const MapView = ({ mapData }) => {
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [sosBatons, setSosBatons] = useState(new Set());
  const [blink, setBlink]         = useState(false);

  useEffect(() => {
  const activeSOS = (mapData || [])
    .filter(b => b.sosTriggered || b.panicTriggered)
    .map(b => b.batonId);

  setSosBatons(new Set(activeSOS));

  if (activeSOS.length > 0) {
    const interval = setInterval(() => {
      setBlink(prev => !prev);
    }, 500);
    return () => clearInterval(interval); 
  } else {
    setBlink(false);
  }
}, [mapData]);

  const batons = useMemo(() => {
    if (mapData?.length > 0) {
      const dataArray = Array.isArray(mapData) ? mapData : [];
const onDutyOnly = dataArray.filter(b => b.status === 'On Duty');
const sosBatons  = onDutyOnly.filter(b => b.sosTriggered || b.panicTriggered);
const regular    = onDutyOnly.filter(b => !b.sosTriggered && !b.panicTriggered);
      return [...sosBatons, ...regular].map((b, i) => ({
        id: i,
        batonId: b.batonId,
        officer: b.assignedTo,
        unit: b.unit,
        lat: b.location?.lat || 16.50,
        lng: b.location?.lng || 80.64,
        status: b.sosTriggered || b.panicTriggered ? 'sos' : b.status,
      }));
    }
    return STATIC_BATONS;
  }, [mapData]);

  useEffect(() => {
    const t = setInterval(() => setLastUpdated(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="map-container">
      <div className="map-header">
        <span className="map-title">LIVE TRACKING – VIJAYAWADA</span>
        <div className="map-header-right">
          <span className="map-duty-count">🟢 {batons.length} On Duty</span>
        </div>
      </div>

      <div className="map-wrapper">
        <MapContainer
          center={[16.5062, 80.6480]}
          zoom={12}
          scrollWheelZoom={true}
          zoomControl={false}
          className="map-canvas"
        >
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* ── Fixes mobile layout size mismatch ── */}
          <MapResizer />

          <ZoomButtons />

          {batons.map((b) => {
            let color = '#64748b';
            if (b.status === 'sos') {
              color = '#ef4444';
            } else if (b.unit === 'Police Constable') {
              color = '#3b82f6';
            } else if (b.unit === 'Shakthi Team') {
              color = '#ec4899';
            } else if (b.unit === 'Traffic Constable') {
              color = '#dbc052';
            }

            return (
              <CircleMarker
                key={b.id}
                center={[b.lat, b.lng]}
                radius={b.status === 'sos' || sosBatons.has(b.batonId) ? (blink ? 12 : 7) : 6}
                pathOptions={{
                  color:       b.status === 'sos' || sosBatons.has(b.batonId) ? '#ff0000' : color,
                  fillColor:   b.status === 'sos' || sosBatons.has(b.batonId) ? '#ff0000' : color,
                  fillOpacity: b.status === 'sos' || sosBatons.has(b.batonId) ? (blink ? 1 : 0.3) : 0.9,
                  weight:      2,
                }}
              >
                <Popup>
                  <strong>{b.batonId || `VJW-${String(b.id).padStart(4,'0')}`}</strong><br />
                  Officer: {b.officer || 'Unknown'}<br />
                  Unit: {b.unit || '—'}<br />
                  Status: {b.status === 'sos' ? '🆘 SOS Triggered' : b.status}
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>

        <div className="map-footer">
          <span>Live Location</span>
          <span style={{ marginLeft: 'auto' }}>
            ⏱ Last Updated: {lastUpdated.toLocaleTimeString('en-IN', {
              hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
            })}
          </span>
        </div>

        <div className="map-unit-legend">
          <div className="mul-title">Unit Legend</div>
          <div className="mul-item">
            <span className="mul-dot" style={{ background: '#3b82f6' }} />
            <span>Police Constable</span>
          </div>
          <div className="mul-item">
            <span className="mul-dot" style={{ background: '#ec4899' }} />
            <span>Shakthi Team</span>
          </div>
          <div className="mul-item">
            <span className="mul-dot" style={{ background: '#dbc052' }} />
            <span>Traffic Constable</span>
          </div>
          <div className="mul-item">
            <span className="mul-dot" style={{ background: '#ef4444' }} />
            <span>SOS / Panic</span>
          </div>
        </div>
      </div>
    </div> 
  );
};

export default MapView;
